import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Camera, Search, PackageX, Star, ArrowRight, SlidersHorizontal, LayoutGrid, List as ListIcon, X, ChevronLeft, ChevronRight, ChevronRight as CaretRight, MoreHorizontal } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORIES = [
  { v: "", label: "All" },
  { v: "camera", label: "Cameras" },
  { v: "alarm", label: "Alarms" },
  { v: "intercom", label: "Intercom" },
  { v: "accessory,dvr", label: "Accessories" },
];

const PAGE_SIZE = 24;

// Builds a compact page-number list with ellipses, e.g. 1 … 4 5 [6] 7 8 … 20
function pageRange(current, total) {
  const pages = new Set([1, total, current, current - 1, current + 1]);
  return [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
}

export default function ProductsPage() {
  const [params, setParams] = useSearchParams();

  // URL is the single source of truth for filters, so refresh/share/back all work.
  const qParam = params.get("q") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand_id") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

  const [qInput, setQInput] = useState(qParam);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [brands, setBrands] = useState([]);
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const debounceRef = useRef(null);
  const topRef = useRef(null);

  useEffect(() => { setQInput(qParam); }, [qParam]);

  const updateParams = useCallback((patch, resetPage = true) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false) next.delete(k);
      else next.set(k, v === true ? "1" : v);
    });
    if (resetPage) next.delete("page");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE, sort: "newest" };
      if (qParam) query.q = qParam;
      if (category) query.category = category;
      if (brand) query.brand_id = brand;
      const countQuery = { ...query };
      delete countQuery.limit; delete countQuery.skip; delete countQuery.sort;
      const [p, c] = await Promise.all([
        api.get("/products", { params: query }),
        api.get("/products/count", { params: countQuery }),
      ]);
      setItems(Array.isArray(p.data) ? p.data : []);
      setTotalCount(c.data.count);
    } finally { setLoading(false); }
  }, [page, qParam, category, brand]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/brands").then(r => setBrands(Array.isArray(r.data) ? r.data : [])).catch(() => setBrands([])); }, []);

  const totalPages = totalCount != null ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1;

  const goToPage = (p) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete("page"); else next.set("page", p);
    setParams(next, { replace: false });
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSearchChange = (val) => {
    setQInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => updateParams({ q: val }), 400);
  };

  const activeCategoryLabel = CATEGORIES.find(c => c.v === category)?.label;

  const activeFilters = [
    category && { key: "category", label: activeCategoryLabel || category, clear: () => updateParams({ category: "" }) },
    brand && { key: "brand", label: brands.find(b => b.brand_id === brand)?.name || "Brand", clear: () => updateParams({ brand_id: "" }) },
    qParam && { key: "q", label: `"${qParam}"`, clear: () => { setQInput(""); updateParams({ q: "" }); } },
  ].filter(Boolean);

  const clearAll = () => {
    setQInput("");
    updateParams({ q: "", category: "", brand_id: "" });
  };

  const BrandSelect = ({ className }) => (
    <Select value={brand || "__all__"} onValueChange={(v) => updateParams({ brand_id: v === "__all__" ? "" : v })}>
      <SelectTrigger className={className} data-testid="products-brand-select"><SelectValue placeholder="Brand" /></SelectTrigger>
      <SelectContent><SelectItem value="__all__">All brands</SelectItem>{brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div ref={topRef} className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-6 sm:py-8">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3" aria-label="Breadcrumb" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-eagle-gold transition-colors">Home</Link>
        <CaretRight className="h-3 w-3" />
        <Link to="/products" className={`hover:text-eagle-gold transition-colors ${!category ? "text-foreground font-medium" : ""}`}>Products</Link>
        {activeCategoryLabel && (
          <>
            <CaretRight className="h-3 w-3" />
            <span className="text-foreground font-medium">{activeCategoryLabel}</span>
          </>
        )}
      </nav>

      {/* Compact header — kept short so products aren't pushed below the fold */}
      <div className="mb-5 sm:mb-6">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-1.5">Catalog</div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1.5 sm:gap-4">
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Security equipment.</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Browse the range — <Link to="/quote" className="text-eagle-gold hover:underline">request a free install quote</Link>.</p>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 sm:pb-0 sm:flex-wrap">
        {CATEGORIES.map(c => (
          <button
            key={c.v}
            onClick={() => updateParams({ category: c.v })}
            data-testid={`category-pill-${(c.v || 'all').replace(/,/g, '-')}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${category === c.v ? "bg-eagle-gold text-eagle-ink shadow-md" : "bg-secondary hover:bg-secondary/70"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={qInput} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search cameras, brands, SKUs…" className="pl-10 h-11 bg-card" data-testid="products-search-input" />
          {qInput && (
            <button onClick={() => onSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Desktop: full filter row. Mobile: single Filters button + view toggle */}
        <div className="hidden sm:flex gap-2.5">
          <BrandSelect className="w-44 h-11 bg-card" />
          <div className="flex gap-1 bg-card border border-border rounded-md p-1">
            <button type="button" onClick={() => setView("grid")} data-testid="view-grid" className={`h-9 w-9 rounded flex items-center justify-center transition-colors ${view === "grid" ? "bg-eagle-gold text-eagle-ink" : "hover:bg-secondary"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button type="button" onClick={() => setView("list")} data-testid="view-list" className={`h-9 w-9 rounded flex items-center justify-center transition-colors ${view === "list" ? "bg-eagle-gold text-eagle-ink" : "hover:bg-secondary"}`}><ListIcon className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="flex sm:hidden gap-2.5">
          <Button variant="outline" onClick={() => setMobileFiltersOpen(true)} className="flex-1 h-11 bg-card" data-testid="mobile-filters-trigger">
            <SlidersHorizontal className="h-4 w-4 mr-2" /> Filters {activeFilters.length > 0 && <Badge className="ml-2 bg-eagle-gold text-eagle-ink border-0 h-5 px-1.5">{activeFilters.length}</Badge>}
          </Button>
          <div className="flex gap-1 bg-card border border-border rounded-md p-1">
            <button type="button" onClick={() => setView("grid")} data-testid="view-grid-mobile" className={`h-9 w-9 rounded flex items-center justify-center transition-colors ${view === "grid" ? "bg-eagle-gold text-eagle-ink" : "hover:bg-secondary"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button type="button" onClick={() => setView("list")} data-testid="view-list-mobile" className={`h-9 w-9 rounded flex items-center justify-center transition-colors ${view === "list" ? "bg-eagle-gold text-eagle-ink" : "hover:bg-secondary"}`}><ListIcon className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="active-filter-chips">
          {activeFilters.map(f => (
            <button key={f.key} onClick={f.clear} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full bg-secondary hover:bg-secondary/70 text-xs font-medium transition-colors" data-testid={`filter-chip-${f.key}`}>
              {f.label} <X className="h-3 w-3" />
            </button>
          ))}
          <button onClick={clearAll} className="text-xs font-semibold text-eagle-gold hover:underline ml-1" data-testid="clear-all-filters">Clear all</button>
        </div>
      )}

      <div className="text-xs text-muted-foreground mb-4">
        {loading ? "Loading…" : totalCount === 0 ? "0 products" : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} products`}
      </div>

      {loading ? (
        <div className={`grid gap-3 sm:gap-4 ${view === "grid" ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"}`}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl overflow-hidden border border-border/70">
              <Skeleton className="aspect-[5/4] w-full rounded-none" />
              <div className="p-4 space-y-2">
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-5 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 sm:p-16 text-center border-dashed">
          <PackageX className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <div className="font-display text-xl mb-1">No products found</div>
          <p className="text-sm text-muted-foreground mb-4">Try clearing filters or <Link to="/quote" className="text-eagle-gold hover:underline">request a custom quote</Link>.</p>
          {activeFilters.length > 0 && <Button variant="outline" onClick={clearAll} data-testid="empty-state-clear">Clear filters</Button>}
        </Card>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {items.map((p, i) => (
            <motion.div key={p.product_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 8) * 0.03 }}>
              <Link to={`/products/${p.product_id}`} data-testid={`product-card-${p.product_id}`} className="block group relative rounded-2xl overflow-hidden bg-card border border-border/70 hover:border-eagle-gold hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="aspect-[5/4] bg-secondary overflow-hidden relative">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Camera className="h-14 w-14 text-muted-foreground/30" strokeWidth={1} /></div>
                  )}
                  {p.is_featured && (
                    <div className="absolute top-2 left-2 sm:top-3 sm:left-3 inline-flex items-center gap-1 px-2 sm:px-2.5 py-1 rounded-full bg-eagle-gold text-eagle-ink text-[9px] sm:text-[10px] font-bold uppercase tracking-widest shadow"><Star className="h-3 w-3 fill-eagle-ink" />Top pick</div>
                  )}
                </div>
                <div className="p-3 sm:p-4">
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-1 truncate">{p.brand_name || "—"}</div>
                  <div className="font-display font-semibold text-xs sm:text-sm line-clamp-2 mb-1 min-h-[2.2rem] sm:min-h-[2.5rem]">{p.name}</div>
                  {p.sku && <div className="text-[9px] sm:text-[10px] text-muted-foreground mb-2 sm:mb-3 truncate">SKU: {p.sku}</div>}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-eagle-gold">Request Quote</span>
                    <div className="text-eagle-gold text-xs font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all">
                      View <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(p => (
            <Link key={p.product_id} to={`/products/${p.product_id}`} data-testid={`product-row-${p.product_id}`} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-card border border-border/70 hover:border-eagle-gold transition-all">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-secondary rounded-lg overflow-hidden shrink-0">
                {p.images?.[0] ? <img src={p.images[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Camera className="h-8 w-8 text-muted-foreground/30" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-0.5">{p.brand_name}</div>
                <div className="font-display font-semibold text-sm sm:text-base truncate">{p.name}</div>
                {p.sku && <div className="text-[10px] text-muted-foreground">SKU: {p.sku}</div>}
                <div className="hidden sm:block text-xs text-muted-foreground truncate">{p.description}</div>
              </div>
              <div className="text-eagle-gold text-xs font-semibold shrink-0 flex items-center gap-1">
                Request Quote <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination — numbers, prev/next, jump to page */}
      {!loading && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-10" data-testid="pagination">
          <div className="flex items-center gap-1">
            <button onClick={() => goToPage(page - 1)} disabled={page <= 1} className="h-9 w-9 rounded-md border border-border flex items-center justify-center hover:border-eagle-gold disabled:opacity-30 disabled:pointer-events-none transition-colors" data-testid="page-prev" aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </button>
            {pageRange(page, totalPages).map((p, i, arr) => (
              <div key={p} className="flex items-center">
                {i > 0 && arr[i - 1] !== p - 1 && <span className="h-9 w-6 flex items-center justify-center text-muted-foreground"><MoreHorizontal className="h-4 w-4" /></span>}
                <button
                  onClick={() => goToPage(p)}
                  className={`h-9 min-w-9 px-2 rounded-md text-sm font-medium transition-colors ${p === page ? "bg-eagle-gold text-eagle-ink" : "hover:bg-secondary border border-border"}`}
                  data-testid={`page-${p}`}
                  aria-current={p === page ? "page" : undefined}
                >
                  {p}
                </button>
              </div>
            ))}
            <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages} className="h-9 w-9 rounded-md border border-border flex items-center justify-center hover:border-eagle-gold disabled:opacity-30 disabled:pointer-events-none transition-colors" data-testid="page-next" aria-label="Next page">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const val = parseInt(new FormData(e.target).get("jump"), 10);
              if (val >= 1 && val <= totalPages) goToPage(val);
            }}
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <span>Jump to page</span>
            <Input name="jump" type="number" min="1" max={totalPages} defaultValue="" placeholder={String(page)} className="w-16 h-9 text-center" data-testid="jump-to-page-input" />
            <span>of {totalPages}</span>
          </form>
        </div>
      )}

      {/* Mobile filters sheet */}
      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
          <SheetHeader className="mb-4"><SheetTitle>Filters</SheetTitle></SheetHeader>
          <div className="space-y-5 pb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Brand</div>
              <BrandSelect className="w-full h-11" />
            </div>
            <div className="flex gap-2 pt-2">
              {activeFilters.length > 0 && <Button variant="outline" className="flex-1" onClick={() => { clearAll(); setMobileFiltersOpen(false); }}>Clear all</Button>}
              <Button className="flex-1 bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" onClick={() => setMobileFiltersOpen(false)}>Show results</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}